import { useEffect, useRef, useState } from "react";
import { Plus, Edit, Trash, CreditCard } from "@mynaui/icons-react";
import { getAllPaymentAccounts, deletePaymentAccount } from "../../api/paymentAccounts";
import { PaymentAccountDTO, PaymentAccountTypeLabels } from "../../models/IPaymentAccount";
import PaymentAccountDialog from "../PaymentAccountDialog";
import { useAppDispatch } from "../../store/store";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import { Button, Card, ConfirmDialog, DescriptionList, EmptyState, IconButton, Loader, Tag } from "../../ui";
import type { DescriptionItem } from "../../ui";
import "./styles.scss";

type Props = {
  canManage: boolean;
};

const PaymentAccountsManagement = ({ canManage }: Props) => {
  const dispatch = useAppDispatch();
  const hasFetched = useRef(false);

  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<PaymentAccountDTO[]>([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState<PaymentAccountDTO | undefined>(undefined);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // The account the delete confirmation is about (kept while the dialog animates out)
  const [accountToDelete, setAccountToDelete] = useState<PaymentAccountDTO | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await getAllPaymentAccounts();
      setAccounts(res.data || []);
    } catch (error) {
      console.error("Error fetching payment accounts:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchData();
  }, []);

  const handleDialogClose = async (update?: boolean) => {
    setShowCreateDialog(false);
    setEditingAccount(undefined);
    if (update) await fetchData();
  };

  const handleDelete = (account: PaymentAccountDTO) => {
    setAccountToDelete(account);
    setIsDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    const account = accountToDelete;
    if (!account) return;
    setDeletingId(account.id);
    try {
      await deletePaymentAccount(account.id);
      dispatch(setGeneralAlertMessage({ message: "Payment account deleted", type: "success" }));
      await fetchData();
    } catch (error: any) {
      dispatch(
        setGeneralAlertMessage({
          message: error?.response?.data?.message || "Failed to delete account",
          type: "error",
        })
      );
    } finally {
      setDeletingId(null);
    }
  };

  const detailsOf = (account: PaymentAccountDTO): DescriptionItem[] => {
    const items: DescriptionItem[] = [];
    if (account.paymentAddress) items.push({ label: "Address", value: account.paymentAddress });
    if (account.accountNumber) items.push({ label: "Account #", value: account.accountNumber });
    if (account.iban) items.push({ label: "IBAN", value: account.iban, wide: true });
    if (account.bankName) items.push({ label: "Bank", value: account.bankName });
    if (account.currency) items.push({ label: "Currency", value: account.currency });
    if (account.paymentUrl)
      items.push({
        label: "URL",
        wide: true,
        value: (
          <a href={account.paymentUrl} target="_blank" rel="noopener noreferrer" className="account-card__link">
            {account.paymentUrl}
          </a>
        ),
      });
    return items;
  };

  return (
    <Card
      className="payment-accounts-management"
      title="Payment Accounts"
      subtitle={!loading && `${accounts.length} ${accounts.length === 1 ? "account" : "accounts"}`}
      actions={
        canManage &&
        !loading && (
          <Button icon={<Plus size={16} />} onClick={() => setShowCreateDialog(true)}>
            New Account
          </Button>
        )
      }
    >
      {loading ? (
        <Loader />
      ) : accounts.length === 0 ? (
        <EmptyState
          icon={<CreditCard />}
          title="No payment accounts"
          description="Create payment accounts that can be linked to any tournament."
          action={
            canManage && (
              <Button icon={<Plus size={16} />} onClick={() => setShowCreateDialog(true)}>
                Create Account
              </Button>
            )
          }
        />
      ) : (
        <div className="accounts-grid">
          {accounts.map((account) => {
            const details = detailsOf(account);
            return (
              <div key={account.id} className={`account-card ${!account.isActive ? "inactive" : ""}`}>
                <div className="account-card__head">
                  <span className="account-card__icon" aria-hidden="true">
                    <CreditCard size={18} />
                  </span>
                  <div className="account-card__titles">
                    <h4>{account.accountName}</h4>
                    <p className="account-card__type">{PaymentAccountTypeLabels[account.accountType]}</p>
                  </div>
                  {canManage && (
                    <div className="account-card__actions">
                      <IconButton label="Edit" size="sm" icon={<Edit size={16} />} onClick={() => setEditingAccount(account)} />
                      <IconButton
                        label="Delete"
                        size="sm"
                        variant="danger"
                        icon={<Trash size={16} />}
                        onClick={() => handleDelete(account)}
                        disabled={deletingId === account.id}
                      />
                    </div>
                  )}
                </div>

                {details.length > 0 && <DescriptionList className="account-card__body" columns={2} items={details} />}

                <div className="account-card__foot">
                  <Tag tone={account.isActive ? "ok" : "neutral"}>{account.isActive ? "Active" : "Inactive"}</Tag>
                  {account.tournaments && account.tournaments.length > 0 && (
                    <span className="account-card__linked">
                      Linked to {account.tournaments.length} tournament{account.tournaments.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(showCreateDialog || editingAccount) && (
        <PaymentAccountDialog account={editingAccount} isOpen={true} onClose={handleDialogClose} />
      )}

      <ConfirmDialog
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
        title={`Delete "${accountToDelete?.accountName ?? ""}"?`}
        message="This cannot be undone."
        confirmLabel="Delete"
      />
    </Card>
  );
};

export default PaymentAccountsManagement;
